import { memo, useMemo, useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { computeDigest } from '../lib/computeDigest'
import type { Post } from '../model/contracts'

const STORAGE_KEY = 'toimDigestOpen'

function readStoredOpen(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw === null ? false : raw === 'true'
  } catch {
    return false
  }
}

function writeStoredOpen(value: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(value))
  } catch {
    // ignore
  }
}

interface ToimDigestPanelProps {
  posts: Post[]
}

export const ToimDigestPanel = memo(function ToimDigestPanel({
  posts,
}: ToimDigestPanelProps) {
  const { t } = useTranslation('feed')
  const [open, setOpen] = useState<boolean>(readStoredOpen)

  const digest = useMemo(() => computeDigest(posts), [posts])

  const handleToggle = () => {
    const next = !open
    setOpen(next)
    writeStoredOpen(next)
  }

  if (digest.totalCount === 0) {
    return (
      <div className="rounded-xl border border-border bg-card px-4 py-3 text-sm text-muted-foreground">
        {t('toim.empty')}
      </div>
    )
  }

  const { sentimentSplit, topKeywords } = digest
  const sentimentTotal = sentimentSplit.pos + sentimentSplit.neg + sentimentSplit.neu
  const posWidth = sentimentTotal > 0 ? (sentimentSplit.pos / sentimentTotal) * 100 : 0
  const negWidth = sentimentTotal > 0 ? (sentimentSplit.neg / sentimentTotal) * 100 : 0
  const neuWidth = sentimentTotal > 0 ? (sentimentSplit.neu / sentimentTotal) * 100 : 0

  const maxKeywordCount = topKeywords.length > 0 ? topKeywords[0].count : 1

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <button
        type="button"
        onClick={handleToggle}
        className="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-muted/30"
        aria-expanded={open}
      >
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-foreground">{t('toim.title')}</span>
          <span className="text-xs text-muted-foreground">
            {t('toim.totalCount', { count: digest.totalCount })}
          </span>
        </div>
        <span
          className="text-muted-foreground"
          aria-label={open ? t('toim.collapse') : t('toim.expand')}
        >
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </span>
      </button>

      {open && (
        <div className="flex flex-col gap-4 px-4 pb-4">
          <div>
            <p className="mb-2 text-xs font-medium text-muted-foreground">
              {t('toim.sentiment')}
            </p>
            <div className="flex flex-col gap-1.5">
              <SentimentBar
                label={t('sentimentFilter.positive')}
                width={posWidth}
                barClass="bg-sentiment-positive"
                count={sentimentSplit.pos}
              />
              <SentimentBar
                label={t('sentimentFilter.negative')}
                width={negWidth}
                barClass="bg-sentiment-negative"
                count={sentimentSplit.neg}
              />
              <SentimentBar
                label={t('sentimentFilter.neutral')}
                width={neuWidth}
                barClass="bg-sentiment-neutral"
                count={sentimentSplit.neu}
              />
            </div>
          </div>

          {topKeywords.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-medium text-muted-foreground">
                {t('toim.topKeywords')}
              </p>
              <div className="flex flex-col gap-1">
                {topKeywords.map(({ label, count }) => (
                  <KeywordBar
                    key={label}
                    label={label}
                    count={count}
                    max={maxKeywordCount}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
})

interface SentimentBarProps {
  label: string
  width: number
  barClass: string
  count: number
}

function SentimentBar({ label, width, barClass, count }: SentimentBarProps) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-28 shrink-0 text-xs text-muted-foreground">{label}</span>
      <div className="relative h-2 flex-1 overflow-hidden rounded-full bg-muted/40">
        <div
          className={cn(
            'absolute inset-y-0 left-0 rounded-full transition-all',
            barClass,
          )}
          style={{ width: `${width.toFixed(1)}%` }}
        />
      </div>
      <span className="w-6 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
        {count}
      </span>
    </div>
  )
}

interface KeywordBarProps {
  label: string
  count: number
  max: number
}

function KeywordBar({ label, count, max }: KeywordBarProps) {
  const width = max > 0 ? (count / max) * 100 : 0
  return (
    <div className="flex items-center gap-2">
      <span className="w-28 shrink-0 truncate text-xs text-foreground">{label}</span>
      <div className="relative h-2 flex-1 overflow-hidden rounded-full bg-muted/40">
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-primary/60 transition-all"
          style={{ width: `${width.toFixed(1)}%` }}
        />
      </div>
      <span className="w-6 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
        {count}
      </span>
    </div>
  )
}
